import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Minus,
  PhoneCall,
  ArrowRight,
  Sparkles,
  FileText,
} from 'lucide-react';
import { FAQS_DATA } from '../data/simatechData';
import supportSpecialistCleanImg from '../assets/images/support_specialist_clean.webp';
import { useCms } from '../context/CmsContext';


interface FaqSectionProps {
  onOpenInquiry: (subject?: string) => void;
}

export const FaqSection: React.FC<FaqSectionProps> = ({ onOpenInquiry }) => {
  const { siteData } = useCms();
  const homeData = siteData?.pages?.home;
  const sectionBgColor = homeData?.faqSectionBgColor || '#ebedf4';
  const sectionHeading = homeData?.faqHeading || homeData?.faqSectionHeading || 'Frequently Asked Questions';
  const sectionSubheading = homeData?.faqSubheading || homeData?.faqSectionSubheading || '';

  // Typography Settings
  const faqHeadingFontFamily = homeData?.faqHeadingFontFamily || 'Jost, sans-serif';
  const faqHeadingFontSize = homeData?.faqHeadingFontSize;
  const faqHeadingFontWeight = homeData?.faqHeadingFontWeight || '300';
  const faqHeadingColor = homeData?.faqHeadingColor || '#06242a';

  const faqSubheadingFontFamily = homeData?.faqSubheadingFontFamily || 'inherit';
  const faqSubheadingFontSize = homeData?.faqSubheadingFontSize;
  const faqSubheadingColor = homeData?.faqSubheadingColor || '#475569';

  // Support Desk Card
  const faqSupportImage = homeData?.faqSupportImage || homeData?.faqCardImage || homeData?.faqSupportSpecialistImage || supportSpecialistCleanImg;
  const faqSupportTitle = homeData?.faqCardTitle || homeData?.faqSupportTitle || 'Technical Support Desk';
  const faqSupportPitch = homeData?.faqSupportPitch || 'Speak directly with our lead Manager.';
  const faqSupportBtn1Text = homeData?.faqSupportBtn1Text || 'Book a 15-Min Call';
  const faqSupportBtn2Text = homeData?.faqSupportBtn2Text || 'Inquire Online';
  const faqSupportCardBgColor = homeData?.faqSupportCardBgColor || '#06242a';
  const faqSupportTitleFontFamily = homeData?.faqSupportTitleFontFamily || 'Jost, sans-serif';
  const faqSupportTitleFontSize = homeData?.faqSupportTitleFontSize;
  const faqSupportTitleColor = homeData?.faqSupportTitleColor || '#ffffff';
  const faqSupportPitchColor = homeData?.faqSupportPitchColor || '#d4ebef';

  // Question & Answer Typography
  const faqQuestionFontFamily = homeData?.faqQuestionFontFamily || 'Jost, sans-serif';
  const faqQuestionFontSize = homeData?.faqQuestionFontSize;
  const faqQuestionFontWeight = homeData?.faqQuestionFontWeight;
  const faqQuestionColor = homeData?.faqQuestionColor || '#06242a';

  const faqAnswerFontFamily = homeData?.faqAnswerFontFamily || 'General Sans, sans-serif';
  const faqAnswerFontSize = homeData?.faqAnswerFontSize;
  const faqAnswerColor = homeData?.faqAnswerColor || '#334155';

  // Use CMS faqItems if they exist and are non-empty, else fall back to FAQS_DATA
  const cmsFaqItems = homeData?.faqItems;
  const faqData = cmsFaqItems && cmsFaqItems.length > 0
    ? cmsFaqItems.map((f: any, i: number) => ({ id: `cms-faq-${i}`, question: f.question, answer: f.answer, category: f.category || 'General' }))
    : FAQS_DATA;

  const [openId, setOpenId] = useState<string>(faqData[0]?.id || '');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Dynamically derive category filter pills from available FAQ items
  const uniqueCategories = Array.from(
    new Set(faqData.map((item) => item.category || 'General'))
  ).filter(Boolean);

  const categories = [
    { id: 'all', label: 'All Topics' },
    ...uniqueCategories.map((cat) => ({ id: cat, label: cat })),
  ];

  const filteredFaqs = activeCategory === 'all'
    ? faqData
    : faqData.filter((item) =>
        (item.category || 'General').toLowerCase() === activeCategory.toLowerCase() ||
        (item.category || '').toLowerCase().includes(activeCategory.toLowerCase())
      );


  const toggleAccordion = (id: string) => {
    setOpenId((prev) => (prev === id ? '' : id));
  };

  return (
    <section
      id="faq"
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-24 border-t border-[#1b6f7e]/15 relative overflow-hidden"
      style={{ backgroundColor: sectionBgColor }}
    >
      
      {/* Subtle Background Accent Gradient */}
      <div className="pointer-events-none absolute top-0 right-0 w-96 h-96 bg-[#1b7481]/5 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-96 h-96 bg-[#38c9de]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* ========================================================================= */}
        {/* 1. TOP HEADER & CATEGORY FILTER TABS                                      */}
        {/* ========================================================================= */}
        <div
          className={`flex flex-col md:flex-row md:items-end justify-between mb-10 sm:mb-12 lg:mb-14 pb-6 sm:pb-8 border-b border-slate-300/80 gap-6 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-400 font-semibold mb-2 sm:mb-2.5">
              {homeData?.faqBadge || 'SUPPORT & FAQS'}
            </div>
            <h2
              className="text-[22px] sm:text-[27px] lg:text-[33px] tracking-tight leading-[1.25]"
              style={{
                fontFamily: faqHeadingFontFamily,
                fontSize: faqHeadingFontSize || undefined,
                fontWeight: faqHeadingFontWeight,
                color: faqHeadingColor,
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: sectionHeading }} />
            </h2>
            <div className="h-[1px] w-16 bg-gradient-to-r from-[#1b7481] to-[#38c9de] mt-3" />
            {sectionSubheading && (
              <p
                className="text-sm mt-2"
                style={{
                  fontFamily: faqSubheadingFontFamily,
                  fontSize: faqSubheadingFontSize || undefined,
                  color: faqSubheadingColor,
                }}
                dangerouslySetInnerHTML={{ __html: sectionSubheading }}
              />
            )}
          </div>

          {/* Interactive Topic Filter Tabs */}
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 text-xs font-mono transition-all duration-200 cursor-pointer border ${
                    isActive
                      ? 'bg-[#06242a] text-white border-[#06242a] shadow-sm'
                      : 'bg-white text-[#06242a]/80 border-slate-300 hover:border-slate-400 hover:text-[#06242a]'
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

        </div>

        {/* ========================================================================= */}
        {/* 2. MAIN 2-COLUMN LAYOUT: EXECUTIVE CONSULTATION CARD + ACCORDION STACK     */}
        {/* ========================================================================= */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start transition-all duration-700 delay-150 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          
          {/* LEFT COLUMN: ULTRA-MODERN CONSULTATION CARD */}
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <div
              className="text-white p-7 sm:p-8 border border-white/10 shadow-[0_20px_50px_rgba(6,36,42,0.18)] relative overflow-hidden"
              style={{ backgroundColor: faqSupportCardBgColor }}
            >
              
              {/* Subtle Ambient Mesh */}
              <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 bg-[#38c9de]/15 rounded-full blur-2xl" />

              {/* Unboxed Large Floating Specialist Character (Clean & Watermark-Free) */}
              <div className="flex flex-col items-center text-center mb-6 pt-2">
                <div className="relative w-64 sm:w-72 md:w-80 h-64 sm:h-72 md:h-80 mx-auto flex items-center justify-center group/img">
                  {/* Soft atmospheric ambient glow */}
                  <div className="absolute inset-4 bg-[#38c9de]/25 rounded-full blur-3xl pointer-events-none group-hover/img:bg-[#38c9de]/35 transition-all duration-500" />
                  <img
                    src={faqSupportImage}
                    alt="Technical Consultation Specialist"
                    width={320}
                    height={320}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-contain relative z-10 drop-shadow-[0_16px_36px_rgba(0,0,0,0.6)] group-hover/img:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="mt-2">
                  <h3
                    className="text-xl font-medium"
                    style={{
                      fontFamily: faqSupportTitleFontFamily,
                      fontSize: faqSupportTitleFontSize || undefined,
                      color: faqSupportTitleColor,
                    }}
                  >
                    {faqSupportTitle}
                  </h3>
                </div>
              </div>

              {/* Pitch Subtext */}
              <p
                className="text-[15.5px] font-light leading-relaxed mb-6 text-center"
                style={{ color: faqSupportPitchColor }}
              >
                {faqSupportPitch}
              </p>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  type="button"
                  id="book-15-min-call-btn"
                  onClick={() => onOpenInquiry('Book a 15-min Discovery Call with Lead Engineer')}
                  className="w-full py-3.5 px-6 bg-[#1b7481] hover:bg-[#38c9de] text-white hover:text-[#06242a] text-xs font-mono uppercase tracking-wider font-semibold transition-all duration-300 shadow-md flex items-center justify-center gap-2.5 cursor-pointer group"
                >
                  <PhoneCall className="w-3.5 h-3.5 text-white group-hover:text-[#06242a] transition-transform group-hover:rotate-12" />
                  <span>{faqSupportBtn1Text}</span>
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </button>

                <button
                  type="button"
                  onClick={() => onOpenInquiry('Request Technical Scope Consultation')}
                  className="w-full py-3 px-6 bg-transparent hover:bg-white/10 text-white/90 hover:text-white border border-white/20 text-xs font-mono uppercase tracking-wider font-medium transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <FileText className="w-3.5 h-3.5 text-[#38c9de]" />
                  <span>{faqSupportBtn2Text}</span>
                </button>
              </div>

            </div>
          </div>

          {/* RIGHT COLUMN: ULTRA-MODERN ACCORDION CARDS */}
          <div className="lg:col-span-8 flex flex-col space-y-4">
            {filteredFaqs.map((faq, idx) => {
              const isOpen = openId === faq.id;
              const itemNumber = (idx + 1).toString().padStart(2, '0');

              return (
                <div
                  key={faq.id}
                  id={`faq-card-${faq.id}`}
                  className={`bg-white border transition-all duration-300 ${
                    isOpen
                      ? 'border-l-4 border-l-[#1b7481] border-t-slate-200 border-r-slate-200 border-b-slate-200 shadow-[0_12px_32px_rgba(6,36,42,0.06)]'
                      : 'border-slate-200/90 hover:border-slate-300 shadow-xs'
                  }`}
                >
                  {/* Header Row: Question Title & Toggle */}
                  <button
                    type="button"
                    onClick={() => toggleAccordion(faq.id)}
                    className="w-full text-left p-6 sm:p-7 flex items-start justify-between gap-5 focus:outline-none cursor-pointer select-none group"
                  >
                    <div className="flex items-start gap-4">
                      {/* Monogram Index Number */}
                      <span
                        className={`text-sm font-mono tracking-wider pt-0.5 transition-colors ${
                          isOpen ? 'text-[#1b7481] font-bold' : 'text-slate-400 group-hover:text-[#1b7481]'
                        }`}
                      >
                        {itemNumber}
                      </span>

                      <div>
                        {/* Category Eyebrow Tag */}
                        <div className="mb-1">
                          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 font-medium">
                            {faq.category}
                          </span>
                        </div>

                        {/* Question Title */}
                        <h3
                          className={`text-base sm:text-[17.5px] tracking-tight leading-snug transition-colors ${
                            isOpen
                              ? 'text-[#06242a] font-medium'
                              : 'text-[#06242a]/90 font-normal group-hover:text-[#1b7481]'
                          }`}
                          style={{
                            fontFamily: faqQuestionFontFamily,
                            fontSize: faqQuestionFontSize || undefined,
                            fontWeight: faqQuestionFontWeight || (isOpen ? '500' : '400'),
                            color: isOpen ? faqQuestionColor : undefined,
                          }}
                        >
                          {faq.question}
                        </h3>
                      </div>
                    </div>

                    {/* Interactive Toggle Icon with Micro-Animation */}
                    <div
                      className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 ${
                        isOpen
                          ? 'bg-[#1b7481] border-[#1b7481] text-white rotate-180 shadow-xs'
                          : 'bg-slate-100 border-slate-200 text-slate-500 group-hover:bg-[#1b7481] group-hover:border-[#1b7481] group-hover:text-white'
                      }`}
                    >
                      {isOpen ? (
                        <Minus className="w-4 h-4 stroke-[2]" />
                      ) : (
                        <Plus className="w-4 h-4 stroke-[2]" />
                      )}
                    </div>
                  </button>

                  {/* Body: Revealed Answer */}
                  {isOpen && (
                    <div className="px-6 sm:px-7 pb-7 pt-2 border-t border-slate-100/80 animate-in fade-in-50 duration-300">
                      <p
                        className="text-[15.5px] font-light leading-relaxed mb-4"
                        style={{
                          fontFamily: faqAnswerFontFamily,
                          fontSize: faqAnswerFontSize || undefined,
                          color: faqAnswerColor,
                        }}
                      >
                        {faq.answer}
                      </p>

                      <div className="flex items-center justify-between pt-3 border-t border-dashed border-slate-200">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenInquiry(`Inquiry regarding: ${faq.question}`);
                          }}
                          className="inline-flex items-center gap-1.5 text-xs font-mono text-[#1b7481] hover:text-[#06242a] font-semibold tracking-wide transition-colors cursor-pointer"
                        >
                          <Sparkles className="w-3 h-3 text-[#38c9de]" />
                          <span>Need more details on this? Ask our engineering team &rarr;</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>

      </div>
    </section>
  );
};
